/**
 * Create API service for audio upload, transcription, and documentation generation.
 * Also includes architecture document management API.
 */

import type {
  CreateUploadResponse,
  TranscriptionJob,
  DocumentationRequest,
  ArchitectureDocType,
} from '@/lib/types';
import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

// Extended documentation request with S3 save options
export interface ExtendedDocumentationRequest extends DocumentationRequest {
  saveToArchitecture?: boolean;
  docMetadata?: {
    title: string;
    docType: ArchitectureDocType;
    parentId?: string;
    systemIds?: string[];
    description?: string;
    initiative?: string;
    domain?: string;
    techStack?: string[];
  };
}

// Response from generate documentation with optional save info
export interface GenerateDocsResponse {
  markdown: string;
  saved?: {
    docId: string;
    docType: string;
    s3Key: string;
  };
}

// Architecture document metadata from S3
export interface ArchitectureDocMetadata {
  id: string;
  type: ArchitectureDocType;
  title: string;
  description: string;
  createdAt: string;
  lastUpdated: string;
  status: string;
  createdBy: string;
  parentId?: string;
  systemIds?: string[];
  initiative?: string;
  domain?: string;
  techStack?: string[];
  systemArchitectures?: string[];
  solutionDesigns?: string[];
  adrs?: string[];
}

// List architecture docs response
export interface ArchitectureDocsHierarchy {
  visions: ArchitectureDocMetadata[];
  systems: ArchitectureDocMetadata[];
  solutions: ArchitectureDocMetadata[];
  adrs: ArchitectureDocMetadata[];
  byId: Record<string, ArchitectureDocMetadata>;
}

// Get single document response
export interface ArchitectureDocResponse {
  metadata: ArchitectureDocMetadata;
  markdown: string;
}

// ============================================
// Async Processing Job Types
// ============================================

export type ProcessingJobStatus = 'pending' | 'transcribing' | 'generating' | 'ready' | 'failed';

export interface ProcessingJob {
  jobId: string;
  status: ProcessingJobStatus;
  statusMessage?: string;
  projectId?: string;
  projectName?: string;
  templateId?: string;
  templateName?: string;
  outputFormat?: string;
  documentId?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubmitAsyncJobRequest {
  projectId: string;
  projectName: string;
  templateId: string;
  templateName: string;
  outputFormat: string;
  identityId: string;
  languageCode?: string;
  uploadId?: string;
  s3Key?: string;
  textContent?: string;
  skipTranscription?: boolean;
  attachments?: Array<{
    type: 'photo' | 'note';
    s3Key?: string;
    text?: string;
    timestamp: number;
    index: number;
  }>;
}

export interface SubmitBatchJobRequest {
  projectId: string;
  projectName: string;
  templateSelections: Array<{
    templateId: string;
    templateName: string;
    outputFormat: string;
  }>;
  identityId: string;
  languageCode?: string;
  uploadId?: string;
  s3Key?: string;
  textContent?: string;
  skipTranscription?: boolean;
  attachments?: Array<{
    type: 'photo' | 'note';
    s3Key?: string;
    text?: string;
    timestamp: number;
    index: number;
  }>;
}

export interface SubmitAsyncJobResponse {
  jobId: string;
  status: string;
  message: string;
}

export interface SubmitBatchJobResponse {
  jobIds: string[];
  status: string;
  message: string;
}

export interface RefineDocumentRequest {
  content: string;
  instruction: string;
}

export interface RefineDocumentResponse {
  content: string;
  summary: string;
}

export interface ListJobsResponse {
  jobs: ProcessingJob[];
}

class AddApiService extends BaseApiService {
  private identityId: string | null = null;

  constructor() {
    super(API_URL, 'AddApi');
  }

  setIdentityId(identityId: string) {
    this.identityId = identityId;
  }

  /**
   * Get a presigned URL for uploading a media file to S3.
   */
  async getUploadUrl(filename: string, contentType: string): Promise<CreateUploadResponse> {
    this.log.debug('Requesting upload URL', { action: 'getUploadUrl', filename, contentType });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/add/upload-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ filename, contentType }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get upload URL', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get upload URL');
    }

    this.log.info('Upload URL received', { action: 'getUploadUrl', filename });
    return response.json();
  }

  /**
   * Upload a media file directly to S3 using presigned URL.
   */
  async uploadMedia(
    presignedUrl: string,
    file: Blob,
    contentType: string,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    this.log.debug('Starting S3 upload', { action: 'uploadMedia', contentType, fileSize: file.size });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          this.log.info('S3 upload completed', { action: 'uploadMedia', status: xhr.status });
          resolve();
        } else {
          this.log.error('S3 upload failed', new Error(`HTTP ${xhr.status}`));
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        this.log.error('S3 upload network error', new Error('Network error'));
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    });
  }

  /**
   * Start transcription job for uploaded audio.
   */
  async startTranscription(
    uploadId: string,
    s3Key: string,
    languageCode: string = 'en-US'
  ): Promise<{ jobId: string; outputKey: string }> {
    this.log.debug('Starting transcription', { action: 'startTranscription', uploadId, s3Key, languageCode });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/add/transcribe`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ uploadId, s3Key, languageCode }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to start transcription', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to start transcription');
    }

    this.log.info('Transcription job started', { action: 'startTranscription', uploadId });
    return response.json();
  }

  /**
   * Check transcription job status.
   */
  async checkTranscription(jobId: string): Promise<TranscriptionJob> {
    this.log.debug('Checking transcription status', { action: 'checkTranscription', jobId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/add/transcription/${jobId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to check transcription', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to check transcription status');
    }

    const result = await response.json();
    return result;
  }

  /**
   * Generate documentation from transcript.
   */
  async generateDocumentation(request: ExtendedDocumentationRequest): Promise<GenerateDocsResponse> {
    this.log.debug('Generating documentation', {
      action: 'generateDocumentation',
      outputFormat: request.outputFormat,
      saveToArchitecture: request.saveToArchitecture,
    });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/add/generate-docs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to generate documentation', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to generate documentation');
    }

    this.log.info('Documentation generated', { action: 'generateDocumentation' });
    return response.json();
  }

  /**
   * Refine document content based on user instructions using AI.
   */
  async refineDocument(request: RefineDocumentRequest): Promise<RefineDocumentResponse> {
    this.log.debug('Refining document', { action: 'refineDocument', contentLength: request.content.length });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/add/refine-document`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to refine document', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to refine document');
    }

    this.log.info('Document refined', { action: 'refineDocument' });
    return response.json();
  }

  // ============================================
  // Architecture Documents API
  // ============================================

  async listArchitectureDocs(typeFilter?: ArchitectureDocType): Promise<ArchitectureDocsHierarchy> {
    this.log.debug('Listing architecture docs', { action: 'listArchitectureDocs', typeFilter });
    const headers = await this.getHeaders();
    const params = typeFilter ? `?type=${typeFilter}` : '';

    const response = await fetch(`${this.baseUrl}/architecture/docs${params}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to list architecture docs', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list architecture documents');
    }

    return response.json();
  }

  async getArchitectureDoc(docType: ArchitectureDocType, docId: string): Promise<ArchitectureDocResponse> {
    this.log.debug('Fetching architecture doc', { action: 'getArchitectureDoc', docType, docId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/architecture/docs/${docType}/${docId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get architecture doc', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get architecture document');
    }

    return response.json();
  }

  async getArchDocUploadUrl(
    filename: string,
    contentType: string,
    docType: ArchitectureDocType,
    fileSize: number
  ): Promise<{ docId: string; presignedUrl: string; s3Key: string; expiresIn: number }> {
    this.log.debug('Requesting architecture doc upload URL', { action: 'getArchDocUploadUrl', filename, docType });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/architecture/upload-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ filename, contentType, docType, fileSize }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get upload URL', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get upload URL');
    }

    return response.json();
  }

  async uploadArchDoc(
    presignedUrl: string,
    file: File,
    contentType: string,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    this.log.debug('Starting architecture doc S3 upload', { action: 'uploadArchDoc', contentType, fileSize: file.size });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    });
  }

  async saveArchDoc(params: {
    docId: string;
    docType: ArchitectureDocType;
    title: string;
    description?: string;
    s3Key: string;
    contentType: string;
    parentId?: string;
    systemIds?: string[];
    tags?: string[];
  }): Promise<{
    success: boolean;
    docId: string;
    docType: string;
    title: string;
    metadataKey: string;
    documentKey: string;
  }> {
    this.log.debug('Saving architecture doc metadata', { action: 'saveArchDoc', docId: params.docId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/architecture/save`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to save architecture doc', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to save document metadata');
    }

    return response.json();
  }

  // ============================================
  // Async Processing Jobs API
  // ============================================

  async submitAsyncJob(request: SubmitAsyncJobRequest): Promise<SubmitAsyncJobResponse> {
    this.log.debug('Submitting async job', { action: 'submitAsyncJob', uploadId: request.uploadId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/add/submit-async`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to submit async job', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to submit processing job');
    }

    this.log.info('Async job submitted', { action: 'submitAsyncJob', uploadId: request.uploadId });
    return response.json();
  }

  async submitBatchJob(request: SubmitBatchJobRequest): Promise<SubmitBatchJobResponse> {
    this.log.debug('Submitting batch job', { action: 'submitBatchJob', templates: request.templateSelections.length });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/add/submit-async`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to submit batch job', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to submit processing jobs');
    }

    this.log.info('Batch job submitted', { action: 'submitBatchJob', templates: request.templateSelections.length });
    return response.json();
  }

  async getJobStatus(jobId: string): Promise<ProcessingJob> {
    this.log.debug('Getting job status', { action: 'getJobStatus', jobId });
    const headers = await this.getHeaders();

    const idParam = this.identityId ? `?identityId=${encodeURIComponent(this.identityId)}` : '';
    const response = await fetch(`${this.baseUrl}/add/jobs/${jobId}${idParam}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get job status', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get job status');
    }

    return response.json();
  }

  async listJobs(statusFilter?: string): Promise<ListJobsResponse> {
    this.log.debug('Listing jobs', { action: 'listJobs', statusFilter });
    const headers = await this.getHeaders();
    const searchParams = new URLSearchParams();
    if (statusFilter) searchParams.set('status', statusFilter);
    if (this.identityId) searchParams.set('identityId', this.identityId);
    const params = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const response = await fetch(`${this.baseUrl}/add/jobs${params}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to list jobs', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list jobs');
    }

    return response.json();
  }

  async listPendingJobs(): Promise<ProcessingJob[]> {
    const response = await this.listJobs('pending,transcribing,generating');
    return response.jobs;
  }

  async getTranscription(transcriptionId: string): Promise<{ transcriptionId: string; content: string }> {
    this.log.debug('Getting transcription', { action: 'getTranscription', transcriptionId });
    const headers = await this.getHeaders();

    const idParam = this.identityId ? `?identityId=${encodeURIComponent(this.identityId)}` : '';
    const response = await fetch(`${this.baseUrl}/add/transcriptions/${transcriptionId}${idParam}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get transcription', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get transcription');
    }

    return response.json();
  }
}

// Export singleton instance
export const addApi = new AddApiService();

// Export class for testing or custom instances
export { AddApiService };
